import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";

export default function MindmapTree({ data }) {
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const [focusNodeName, setFocusNodeName] = useState(null); // null = full view
  const [history, setHistory] = useState([]); // breadcrumb history
  const zoomRef = useRef(null);
  const EXTRA_RIGHT = 120;
  const EXTRA_BOTTOM = 120;
  const EXTRA_LEFT = 80;
  const EXTRA_TOP = 80;
  // utility: find node object by name (DFS)
  function findNode(objArray, name) {
    for (const n of objArray) {
      if (n.name === name) return n;
      if (n.children?.length) {
        const found = findNode(n.children, name);
        if (found) return found;
      }
    }
    return null;
  }

  useEffect(() => {
    if (!data || !svgRef.current) return;
    const svgEl = svgRef.current;
    const svg = d3.select(svgEl);
    svg.selectAll("*").remove();
    const width = svgEl.clientWidth || 900;
    const height = 640;

    // choose the dataset to render: if focusNodeName set, find that subtree
    const sourceRootData =
      focusNodeName && findNode(data, focusNodeName)
        ? findNode(data, focusNodeName)
        : { name: "root", children: data };

    // convert to d3.hierarchy
    const root = d3.hierarchy(sourceRootData);

    // layout: radial-ish top-down but using tree to keep it simple & compact
    // nodeSize controls separation; tuned for premium, non-overlap
    const nodeSize = [120, 110]; // [horizontal, vertical]
    const tree = d3
      .tree()
      .nodeSize(nodeSize)
      .separation((a, b) => (a.parent === b.parent ? 1.2 : 1.6));
    tree(root);

    // svg defs: gradients + shadow
    const defs = svg.append("defs");

    defs
      .append("linearGradient")
      .attr("id", "gradCorp")
      .attr("x1", "0%")
      .attr("x2", "100%")
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#005A9C");
    defs
      .select("#gradCorp")
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "#005A9C");

    defs
      .append("linearGradient")
      .attr("id", "gradTrust")
      .attr("x1", "0%")
      .attr("x2", "100%")
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#DB2777");
    defs
      .select("#gradTrust")
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "#DB2777");

    defs
      .append("linearGradient")
      .attr("id", "gradInd")
      .attr("x1", "0%")
      .attr("x2", "100%")
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#047857");
    defs
      .select("#gradInd")
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "#047857");

    // soft shadow filter
    defs
      .append("filter")
      .attr("id", "softShadow")
      .append("feDropShadow")
      .attr("dx", 0)
      .attr("dy", 2)
      .attr("stdDeviation", 4)
      .attr("flood-opacity", 0.18);

    // main group with padding
    const paddingX = 80;
    const paddingY = 40;
    const g = svg
      .append("g")
      .attr("transform", `translate(${paddingX}, ${paddingY})`);

    // zoom + pan
    const zoom = d3
      .zoom()
      .scaleExtent([0.5, 2.2])
      .on("zoom", (event) => {
        g.attr(
          "transform",
          `translate(${paddingX + event.transform.x}, ${
            paddingY + event.transform.y
          }) scale(${event.transform.k})`
        );
      });

    svg.call(zoom);
    zoomRef.current = zoom;

    // compute bounds to center tree horizontally
    const xs = root.descendants().map((d) => d.x);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const centerOffset = (width - (maxX - minX)) / 2 - minX;

    // draw links (curved)
    const link = d3
      .linkVertical()
      .x((d) => d.x + centerOffset)
      .y((d) => d.y);

    const links = g
      .selectAll("path.link")
      .data(root.links())
      .join("path")
      .attr("class", "link")
      .attr("d", link)
      .attr("fill", "none")
      .attr("stroke", "#e6e9ee")
      .attr("stroke-width", 2);

    // nodes
    const nodes = g
      .selectAll("g.node")
      .data(root.descendants().filter((d) => d.data.name !== "root"))
      .join("g")
      .attr("class", "node")
      .attr("transform", (d) => `translate(${d.x + centerOffset}, ${d.y})`)
      .style("cursor", "pointer");

    // hover/tooltip handlers
    const tooltip = d3.select(tooltipRef.current);
    nodes.each(function (d) {
      const node = d3.select(this);
      const t = d.data.type || "Individual";

      // shape sizes
      const shapeW = 110;
      const shapeH = 56;
      const circleR = 34;

      // Group for shape + text
      const body = node.append("g").attr("class", "node-body");

      // Draw shapes
      if (t === "Corporate") {
        body
          .append("rect")
          .attr("x", -shapeW / 2)
          .attr("y", -shapeH / 2)
          .attr("rx", 12)
          .attr("width", shapeW)
          .attr("height", shapeH)
          .attr("fill", "url(#gradCorp)")
          .attr("filter", "url(#softShadow)");
      } else if (t === "Trust") {
        body
          .append("polygon")
          .attr("points", `0,-36 44,28 -44,28`)
          .attr("fill", "url(#gradTrust)")
          .attr("filter", "url(#softShadow)");
      } else {
        body
          .append("circle")
          .attr("r", circleR)
          .attr("fill", "url(#gradInd)")
          .attr("filter", "url(#softShadow)");
      }

      // Inner border
      if (t === "Corporate") {
        body
          .append("rect")
          .attr("x", -shapeW / 2)
          .attr("y", -shapeH / 2)
          .attr("rx", 12)
          .attr("width", shapeW)
          .attr("height", shapeH)
          .attr("fill", "none")
          .attr("stroke", "rgba(255,255,255,0.18)")
          .attr("stroke-width", 1.5);
      } else if (t === "Trust") {
        body
          .append("polygon")
          .attr("points", `0,-36 44,28 -44,28`)
          .attr("fill", "none")
          .attr("stroke", "rgba(255,255,255,0.18)")
          .attr("stroke-width", 1.5);
      } else {
        body
          .append("circle")
          .attr("r", circleR)
          .attr("fill", "none")
          .attr("stroke", "rgba(255,255,255,0.18)")
          .attr("stroke-width", 1.5);
      }

      // TEXT inside shape
      let text = "";
      if (t === "Trust") {
        text = body
          .append("text")
          .attr("text-anchor", "middle")
          .attr("fill", "#fff")
          .style("font-family", "Work Sans, sans-serif")
          .style("font-weight", 500)
          .style("font-size", 10)
          .attr("dy", 6);
      } else {
        text = body
          .append("text")
          .attr("text-anchor", "middle")
          .attr("fill", "#fff")
          .style("font-family", "Work Sans, sans-serif")
          .style("font-weight", 500)
          .style("font-size", 10);
      }
      const words = (d.data.name || "").split(/\s+/);
      const lineHeight = 14; // px
      let lines = [];
      let currentLine = "";

      // Shape-specific max width function
      function getMaxWidth(lineIndex) {
        if (t === "Corporate") return shapeW - 12;
        if (t === "Individual") return circleR * 2 - 12;

        if (t === "Trust") {
          // Triangle points: top(0,-36), bottom left(-44,28), bottom right(44,28)
          const topY = -36;
          const bottomY = 28;
          const triHeight = bottomY - topY; // 64
          const baseWidth = 88; // width at bottom (44 - (-44))

          // actual text line y inside the triangle
          const lineY = topY + (lineIndex + 1) * lineHeight;

          // normalize 0 (top) → 1 (bottom)
          const p = Math.min(1, Math.max(0, (lineY - topY) / triHeight));

          // width narrows linearly toward top
          const maxW = baseWidth * p;

          return Math.max(30, maxW - 12); // must leave padding
        }

        return 80;
      }

      // Split very long words into smaller chunks
      const splitWords = [];
      words.forEach((word) => {
        let temp = "";
        for (let ch of word) {
          const test = temp + ch;
          text.text(test);
          if (text.node().getComputedTextLength() > getMaxWidth(lines.length)) {
            if (temp) splitWords.push(temp);
            temp = ch; // start new segment
          } else {
            temp = test;
          }
        }
        if (temp) splitWords.push(temp);
      });

      // Assemble lines respecting max width per line
      splitWords.forEach((word) => {
        const testLine = currentLine ? currentLine + " " + word : word;
        const maxWidth = getMaxWidth(lines.length, 20);
        text.text(testLine);
        if (text.node().getComputedTextLength() > maxWidth) {
          if (currentLine) lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      });
      if (currentLine) lines.push(currentLine);

      // Clear temp text
      text.text("");

      // Vertical centering
      const totalHeight = lines.length * lineHeight;
      let yStart = -totalHeight / 2 + lineHeight / 2;

      lines.forEach((lineText, i) => {
        text
          .append("tspan")
          .attr("x", 0)
          .attr("y", yStart + i * lineHeight)
          .attr("text-anchor", "middle")
          .text(lineText);
      });
    });

    // Hover & tooltip behavior, highlight & scale
    nodes
      .on("mouseenter", function (event, d) {
        const n = d3.select(this);
        // subtle scale + glow
        n.selectAll("*")
          .transition()
          .duration(180)
          .attr("transform", "scale(1.03)");
        n.raise();

        tooltip
          .style("opacity", 1)
          .html(
            `<strong>${
              d.data.name
            }</strong><div style="font-size:11px;opacity:0.9">${
              d.data.type || "Individual"
            }</div>`
          );

        const [mx, my] = d3.pointer(event, svg.node());
        tooltip.style("left", `${mx + 18}px`).style("top", `${my + 18}px`);
      })
      .on("mousemove", function (event) {
        const [mx, my] = d3.pointer(event, svg.node());
        tooltip.style("left", `${mx + 18}px`).style("top", `${my + 18}px`);
      })
      .on("mouseleave", function () {
        const n = d3.select(this);
        n.selectAll("*")
          .transition()
          .duration(180)
          .attr("transform", "scale(1)");
        tooltip.style("opacity", 0);
      })
      .on("click", function (event, d) {
        // focus behavior: set focus to clicked node name (unless already focused)
        if (d.data.name && d.data.name === focusNodeName) {
          // already focused -> go back to full view (pop history)
          if (history.length) {
            const prev = history[history.length - 1];
            setHistory((h) => h.slice(0, -1));
            setFocusNodeName(prev || null);
          } else {
            setFocusNodeName(null);
          }
        } else {
          // push current focus to history and set new focus
          setHistory((h) => [...h, focusNodeName]);
          setFocusNodeName(d.data.name);
        }
      });

    // center initial view by translating group so top looks centered
    // we already applied centerOffset to x positions; ensure minimal pan so top visible
    svg.call(d3.zoom().transform, d3.zoomIdentity.translate(0, 0).scale(1));

    // cleanup on unmount
    return () => {
      svg.selectAll("*").remove();
    };
  }, [data, focusNodeName, history]);

  // Breadcrumb / controls UI (simple)
  const back = () => {
    if (!history.length) {
      setFocusNodeName(null);
      setHistory([]);
      return;
    }
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setFocusNodeName(prev || null);
  };
  function serializeSvg(svg) {
    const clone = svg.cloneNode(true);

    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");

    // Force white background
    clone.style.background = "white";

    return new XMLSerializer().serializeToString(clone);
  }
  const inlineAllStyles = (sourceSvg, targetSvg) => {
    const sourceElements = sourceSvg.querySelectorAll("*");
    const targetElements = targetSvg.querySelectorAll("*");

    sourceElements.forEach((srcEl, i) => {
      const tgtEl = targetElements[i];
      const computed = window.getComputedStyle(srcEl);

      let style = "";
      for (let j = 0; j < computed.length; j++) {
        const prop = computed[j];
        style += `${prop}:${computed.getPropertyValue(prop)};`;
      }
      tgtEl.setAttribute("style", style);
    });
  };

  const cloneSvgForExport = (svg) => {
    const clone = svg.cloneNode(true);

    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
    clone.style.background = "white";

    clone.style.position = "absolute";
    clone.style.left = "-9999px";
    clone.style.top = "-9999px";

    document.body.appendChild(clone);

    // 🔥 Inline styles from original SVG
    inlineAllStyles(svg, clone);

    return clone;
  };

  const createExportSvg = (svg) => {
    const bbox = svg.getBBox();

    const exportSvg = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg"
    );

    exportSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const exportWidth = bbox.width + EXTRA_LEFT + EXTRA_RIGHT;
    const exportHeight = bbox.height + EXTRA_TOP + EXTRA_BOTTOM;

    exportSvg.setAttribute("width", exportWidth);
    exportSvg.setAttribute("height", exportHeight);

    exportSvg.setAttribute(
      "viewBox",
      `${bbox.x - EXTRA_LEFT} ${
        bbox.y - EXTRA_TOP
      } ${exportWidth} ${exportHeight}`
    );

    exportSvg.style.background = "white";

    return exportSvg;
  };
  const extractGraphGroup = (svg) => {
    // IMPORTANT: select the group that contains nodes + links
    const graphGroup =
      svg.querySelector("g") || svg.querySelector("g.zoom-layer");

    return graphGroup.cloneNode(true);
  };
  const cloneDefs = (svg) => {
    const defs = svg.querySelector("defs");
    return defs ? defs.cloneNode(true) : null;
  };
  const downloadGraph = () => {
    const svg = svgRef.current;
    if (!svg) return;

    // 🔹 Find the MAIN graph group (this is critical)
    const graphGroup = svg.querySelector("g"); // first <g> contains everything
    if (!graphGroup) return;

    const bbox = graphGroup.getBBox();

    // 🔹 Create export SVG
    const exportSvg = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg"
    );

    exportSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const exportWidth = bbox.width + EXTRA_LEFT + EXTRA_RIGHT;
    const exportHeight = bbox.height + EXTRA_TOP + EXTRA_BOTTOM;

    exportSvg.setAttribute("width", exportWidth);
    exportSvg.setAttribute("height", exportHeight);

    exportSvg.setAttribute(
      "viewBox",
      `${bbox.x - EXTRA_LEFT} ${
        bbox.y - EXTRA_TOP
      } ${exportWidth} ${exportHeight}`
    );

    exportSvg.style.background = "white";

    // ✅ COPY DEFS (THIS FIXES NODES)
    const defsClone = cloneDefs(svg);
    if (defsClone) exportSvg.appendChild(defsClone);

    // ✅ Clone graph content
    const graphClone = graphGroup.cloneNode(true);

    // 🚫 Remove zoom transform from clone ONLY
    graphClone.setAttribute(
      "transform",
      `translate(${EXTRA_LEFT}, ${EXTRA_TOP})`
    );
    exportSvg.appendChild(graphClone);
    document.body.appendChild(exportSvg);

    // 🔹 Serialize
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(exportSvg);

    const blob = new Blob([svgStr], {
      type: "image/svg+xml;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);

    // 🔹 SVG → PNG
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = exportSvg.width.baseVal.value * 2;
      canvas.height = exportSvg.height.baseVal.value * 2;

      const ctx = canvas.getContext("2d");
      ctx.scale(2, 2);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      URL.revokeObjectURL(url);
      document.body.removeChild(exportSvg);

      canvas.toBlob((png) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(png);
        a.download = "full-tree.png";
        a.click();
        URL.revokeObjectURL(a.href);
      });
    };

    img.src = url;
  };

  return (
    <div className="relative w-full h-full bg-white rounded-xl shadow-sm p-2">
      {/* Controls */}
      <div className="flex items-center gap-3 mb-2">
        {history.length > 0 && (
          <button
            onClick={back}
            className="rounded-lg border px-3 py-1 text-labelColour self-start sm:self-start font-medium text-sm underline flex items-center sm:mt-0 sm:ml-4"
          >
            <img
              src="/bottom-back.svg"
              alt="Portfolio Icon"
              className="w-4 h-4 mr-1"
            />{" "}
            Back
          </button>
        )}
        <button
          onClick={downloadGraph}
          className="rounded-lg border px-3 py-1 text-labelColour self-start sm:self-start font-medium text-sm underline flex items-center sm:mt-0 sm:ml-4"
        >
          <img
            src="/document-download-grey.svg"
            alt="Portfolio Icon"
            className="w-4 h-4 mr-1"
          />{" "}
          Download
        </button>

        {focusNodeName && (
          <div className="ml-auto text-sm font-medium text-black">
            Shareholder:
            <span className="text-gray-600"> {focusNodeName}</span>
          </div>
        )}
      </div>

      {/* SVG container */}
      <div className="w-full  overflow-hidden" style={{ height: 640 }}>
        <svg ref={svgRef} className="w-full h-full cursor-pointer" />
      </div>
      {/* Legend */}
      <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm p-3 rounded-lg shadow-xl text-sm flex flex-col gap-2 w-40">
        <div className="flex items-center gap-2">
          <svg width="24" height="24">
            <rect width="24" height="14" rx="4" fill="url(#gradCorp)" />
          </svg>
          <span>Corporate</span>
        </div>
        <div className="flex items-center gap-2">
          <svg width="24" height="24">
            <polygon points="12,2 22,22 2,22" fill="url(#gradTrust)" />
          </svg>
          <span>Trust</span>
        </div>
        <div className="flex items-center gap-2">
          <svg width="24" height="24">
            <circle cx="12" cy="12" r="10" fill="url(#gradInd)" />
          </svg>
          <span>Individual</span>
        </div>
      </div>

      {/* Tooltip (HTML overlay) */}
      <div
        ref={tooltipRef}
        style={{
          position: "absolute",
          pointerEvents: "none",
          background: "rgba(17,24,39,0.95)",
          color: "white",
          padding: "8px 10px",
          borderRadius: 8,
          fontSize: 12,
          boxShadow: "0 6px 20px rgba(2,6,23,0.2)",
          opacity: 0,
          transition: "opacity 120ms ease",
        }}
      />
    </div>
  );
}
